import { useMutation, useQueryClient, UseMutationOptions } from '@tanstack/react-query';
import { Habit } from '../types';
import { createHabit, updateHabit, deleteHabit } from '../services/api';

// Type for the data sent to the create API endpoint
type CreateHabitData = Omit<Habit, 'id' | 'user' | 'created_at' | 'updated_at'>;
// Type for the data sent to the update API endpoint
type UpdateHabitDataPayload = Partial<Omit<Habit, 'id' | 'user' | 'created_at' | 'updated_at'>>;
type UpdateHabitData = UpdateHabitDataPayload & { id: number };

// Hook for creating habits
export const useCreateHabit = (
    options?: Omit<UseMutationOptions<Habit, Error, CreateHabitData>, 'mutationFn'>
) => {
    const queryClient = useQueryClient();
    return useMutation<Habit, Error, CreateHabitData>({
        mutationFn: createHabit,
        ...options,
        onSuccess: (data, variables, context) => {
            // Invalidate habits query to refetch the list
            queryClient.invalidateQueries({ queryKey: ['habits'] });
            // Call original onSuccess if provided
            options?.onSuccess?.(data, variables, context);
        },
    });
};

// Hook for updating habits
export const useUpdateHabit = (
    options?: Omit<UseMutationOptions<Habit, Error, UpdateHabitData>, 'mutationFn'>
) => {
    const queryClient = useQueryClient();
    return useMutation<Habit, Error, UpdateHabitData>({
        mutationFn: (data) => updateHabit(data.id, data), // Pass ID separately
        ...options,
        onSuccess: (data, variables, context) => {
            // Invalidate habits query to refetch the list
            queryClient.invalidateQueries({ queryKey: ['habits'] });
            // Optionally, update the specific habit query cache if you have one
            // queryClient.setQueryData(['habit', { id: variables.id }], data);
            options?.onSuccess?.(data, variables, context);
        },
    });
};

// Hook for deleting habits
export const useDeleteHabit = (
    options?: Omit<UseMutationOptions<void, Error, number>, 'mutationFn'>
) => {
    const queryClient = useQueryClient();
    return useMutation<void, Error, number>({
        mutationFn: deleteHabit, // Takes habitId as argument
        ...options,
        onSuccess: (data, habitId, context) => {
            // Invalidate habits query to refetch the list
            queryClient.invalidateQueries({ queryKey: ['habits'] });
            // Optionally, remove the specific habit from the query cache
            // queryClient.removeQueries(['habit', { id: habitId }]);
            options?.onSuccess?.(data, habitId, context);
        },
    });
};

// TODO: Implement useHabits, useHabitCompletions hooks properly if needed (useQuery is often used directly in components)

// Add other habit-related hooks here later (useCreateHabit, useUpdateHabit, useHabits, etc.) 